import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '../api';
import { formatPhoneNumber } from '../utils/formatters';

function DefaultedLoansReport() {
  const navigate = useNavigate();
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadDefaultedLoans();
  }, []);

  const loadDefaultedLoans = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/loans`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('adminToken')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch loans');
      }

      const data = await response.json();
      // Filter for defaulted loans only
      const defaultedLoans = data.filter(loan => loan.status === 'defaulted');
      setLoans(defaultedLoans);
    } catch (err) {
      setError('Failed to load defaulted loans');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Calculate totals
  const totalRecoveryCosts = loans.reduce((sum, loan) => sum + parseFloat(loan.recovery_costs || 0), 0);
  const totalNetRecovery = loans.reduce((sum, loan) => sum + parseFloat(loan.net_recovery || 0), 0);
  const totalBalanceLost = loans.reduce((sum, loan) => sum + parseFloat(loan.balance_remaining || 0), 0);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading defaulted loans report...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: '20px', maxWidth: '95%', margin: '0 auto' }}>
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', maxWidth: '95%', margin: '0 auto' }}>
      <div style={{ marginBottom: '2rem' }}>
        <button onClick={() => navigate('/admin/loans')} className="admin-hub-back-btn">
          <span className="admin-hub-back-arrow">←</span>
          Back to Loan Management
        </button>
        <h1 className="admin-page-title">📊 Defaulted Loans Report</h1>
      </div>

      {/* Summary Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'var(--color-text-light)' }}>Total Defaulted Loans</h3>
          <p style={{ margin: 0, fontSize: '36px', fontWeight: 'bold', color: 'var(--color-error)' }}>
            {loans.length}
          </p>
        </div>

        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'var(--color-text-light)' }}>Total Recovery Costs</h3>
          <p style={{ margin: 0, fontSize: '36px', fontWeight: 'bold', color: 'var(--color-error)' }}>
            ${formatCurrency(totalRecoveryCosts)}
          </p>
          <small style={{ color: 'var(--color-text-muted)' }}>Legal, repo, cleanup</small>
        </div>

        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'var(--color-text-light)' }}>Net Recovery</h3>
          <p style={{ 
            margin: 0, 
            fontSize: '36px', 
            fontWeight: 'bold', 
            color: totalNetRecovery >= 0 ? 'var(--forest-green)' : '#dc3545'
          }}>
            ${formatCurrency(totalNetRecovery)}
          </p>
          <small style={{ color: 'var(--color-text-muted)' }}>Amount kept after costs</small>
        </div>

        <div className="card" style={{ padding: '20px', textAlign: 'center' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: 'var(--color-text-light)' }}>Balance Lost</h3>
          <p style={{ margin: 0, fontSize: '36px', fontWeight: 'bold', color: 'var(--color-error)' }}>
            ${formatCurrency(totalBalanceLost)}
          </p>
          <small style={{ color: 'var(--color-text-muted)' }}>Remaining unpaid balance</small>
        </div>
      </div>

      {/* Defaulted Loans Table */}
      {loans.length === 0 ? (
        <div className="card" style={{ padding: '40px', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--color-brand-text)', marginBottom: '10px' }}>🎉 No Defaulted Loans</h3>
          <p style={{ color: 'var(--color-text-light)' }}>All your loans are either active or paid off!</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--light-green)', borderBottom: '2px solid var(--forest-green)' }}>
                <th style={{ padding: '15px', textAlign: 'left' }}>Customer</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Property</th>
                <th style={{ padding: '15px', textAlign: 'center' }}>Default Date</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Acquisition Cost</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Total Paid</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Balance Lost</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Recovery Costs</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Net Recovery</th>
                <th style={{ padding: '15px', textAlign: 'right' }}>Property Status</th>
                <th style={{ padding: '15px', textAlign: 'left' }}>Notes</th>
              </tr>
            </thead>
            <tbody>
              {loans.map(loan => {
                // A property with no recorded acquisition cost is UNKNOWN, not free.
                // This used to read `parseFloat(x || 0)`, which printed $0.00 in the
                // Acquisition Cost column and then subtracted nothing at all from the
                // profit line -- overstating the recovery on that property by its
                // entire purchase price. Boot used to paper over it by writing a
                // random 35-50% of price into every NULL on startup, which replaced a
                // visibly missing number with an invented one.
                const hasAcquisitionCost = loan.property_acquisition_cost != null &&
                  loan.property_acquisition_cost !== '';
                const acquisitionCost = hasAcquisitionCost
                  ? parseFloat(loan.property_acquisition_cost)
                  : null;
                const totalPaid = parseFloat(loan.total_paid || 0);
                const recoveryCosts = parseFloat(loan.recovery_costs || 0);
                // Not computable without the cost. Rendered as "not recorded" below
                // rather than as a number the figure cannot support.
                const propertyProfit = hasAcquisitionCost
                  ? totalPaid - acquisitionCost - recoveryCosts
                  : null;

                return (
                  <tr key={loan.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <td style={{ padding: '15px' }}>
                      <div style={{ fontWeight: '600' }}>
                        {loan.first_name} {loan.last_name}
                      </div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>{loan.email}</div>
                      {loan.phone && <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>{formatPhoneNumber(loan.phone)}</div>}
                    </td>
                    <td style={{ padding: '15px' }}>
                      <div style={{ fontWeight: '600' }}>{loan.property_title}</div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)' }}>{loan.property_location}</div>
                      {loan.property_apn && <div style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>APN: {loan.property_apn}</div>}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      {loan.default_date ? new Date(loan.default_date).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right', fontWeight: '600' }}>
                      {hasAcquisitionCost
                        ? `$${formatCurrency(acquisitionCost)}`
                        : <span style={{ color: 'var(--color-text-muted)', fontWeight: '400' }}>not recorded</span>}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right', fontWeight: '600', color: 'var(--color-brand-text)' }}>
                      ${formatCurrency(totalPaid)}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right', color: 'var(--color-error)', fontWeight: '600' }}>
                      ${formatCurrency(loan.balance_remaining)}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right', color: 'var(--color-error)' }}>
                      ${formatCurrency(recoveryCosts)}
                    </td>
                    <td style={{ 
                      padding: '15px', 
                      textAlign: 'right', 
                      fontWeight: 'bold',
                      fontSize: '16px',
                      color: (loan.net_recovery || 0) >= 0 ? 'var(--forest-green)' : '#dc3545'
                    }}>
                      ${formatCurrency(loan.net_recovery || 0)}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right' }}>
                      <div style={{ 
                        display: 'inline-block',
                        padding: '4px 12px',
                        borderRadius: '4px',
                        fontSize: '0.85rem',
                        fontWeight: '600',
                        backgroundColor: loan.property_status === 'available' ? '#ffc107' : loan.property_status === 'sold' ? 'var(--forest-green)' : '#666',
                        color: 'white'
                      }}>
                        {loan.property_status === 'available' ? 'Available' : 
                         loan.property_status === 'sold' ? 'Resold' :
                         loan.property_status === 'pending' ? 'Pending' :
                         loan.property_status}
                      </div>
                      {propertyProfit === null ? (
                        <div style={{ fontSize: '0.8rem', marginTop: '5px', color: 'var(--color-text-muted)' }}>
                          profit needs an acquisition cost
                        </div>
                      ) : propertyProfit !== 0 && (
                        <div style={{
                          fontSize: '0.8rem',
                          marginTop: '5px',
                          color: propertyProfit >= 0 ? 'var(--forest-green)' : '#dc3545',
                          fontWeight: '600'
                        }}>
                          {propertyProfit >= 0 ? '+' : '-'}${formatCurrency(Math.abs(propertyProfit))} {propertyProfit >= 0 ? 'profit' : 'loss'}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '15px', fontSize: '0.9rem', color: 'var(--color-text-light)', maxWidth: '200px' }}>
                      {loan.default_notes || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Property Lifecycle Analysis */}
      {loans.length > 0 && (
        <div className="card" style={{ marginTop: '30px', padding: '30px' }}>
          <h2 style={{ color: 'var(--color-brand-text)', marginBottom: '20px' }}>🏘️ Property Lifecycle Analysis</h2>
          <p style={{ color: 'var(--color-text-light)', marginBottom: '20px' }}>
            This section tracks each property's complete financial journey, including all customers who have purchased it.
          </p>
          
          {/* Group loans by property */}
          {(() => {
            const propertyMap = {};
            loans.forEach(loan => {
              const propId = loan.property_id;
              if (!propertyMap[propId]) {
                propertyMap[propId] = {
                  property_id: propId,
                  property_title: loan.property_title,
                  property_apn: loan.property_apn,
                  property_status: loan.property_status,
                  // null means "not recorded", and stays null all the way to the
                  // Net Profit/Loss tile. Coercing it to 0 here reported the whole
                  // of a defaulted property's collections as profit.
                  acquisition_cost: loan.property_acquisition_cost != null &&
                    loan.property_acquisition_cost !== ''
                    ? parseFloat(loan.property_acquisition_cost)
                    : null,
                  loans: []
                };
              }
              propertyMap[propId].loans.push(loan);
            });

            return Object.values(propertyMap).map(property => {
              const totalPaid = property.loans.reduce((sum, l) => sum + parseFloat(l.total_paid || 0), 0);
              const totalRecoveryCosts = property.loans.reduce((sum, l) => sum + parseFloat(l.recovery_costs || 0), 0);
              const hasAcquisitionCost = property.acquisition_cost !== null;
              const netProfit = hasAcquisitionCost
                ? totalPaid - property.acquisition_cost - totalRecoveryCosts
                : null;
              const timesDefaulted = property.loans.length;

              return (
                <div key={property.property_id} style={{ 
                  border: '2px solid var(--color-border)', 
                  borderRadius: '8px', 
                  padding: '20px', 
                  marginBottom: '20px',
                  backgroundColor: netProfit === null ? '#f7f7f7' : netProfit >= 0 ? '#f0f8f0' : '#fff5f5'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '15px', flexWrap: 'wrap', gap: '1rem' }}>
                    <div>
                      <h3 style={{ margin: '0 0 5px 0', color: 'var(--color-brand-text)' }}>
                        {property.property_title}
                      </h3>
                      {property.property_apn && (
                        <div style={{ fontSize: '0.9rem', color: 'var(--color-text-light)' }}>APN: {property.property_apn}</div>
                      )}
                      <div style={{ fontSize: '0.9rem', color: 'var(--color-text-light)', marginTop: '5px' }}>
                        Defaulted {timesDefaulted} time{timesDefaulted > 1 ? 's' : ''}
                      </div>
                    </div>
                    <div style={{ 
                      padding: '8px 16px',
                      borderRadius: '4px',
                      fontSize: '0.9rem',
                      fontWeight: '600',
                      backgroundColor: property.property_status === 'available' ? '#ffc107' : property.property_status === 'sold' ? 'var(--forest-green)' : '#666',
                      color: 'white'
                    }}>
                      {property.property_status === 'available' ? '🏷️ Available for Resale' : 
                       property.property_status === 'sold' ? '✅ Resold' :
                       property.property_status === 'pending' ? '⏳ Pending' :
                       property.property_status}
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '15px', marginTop: '15px' }}>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '5px' }}>Acquisition Cost</div>
                      <div style={{
                        fontSize: hasAcquisitionCost ? '1.2rem' : '1rem',
                        fontWeight: 'bold',
                        color: hasAcquisitionCost ? '#dc3545' : '#999'
                      }}>
                        {hasAcquisitionCost ? `$${formatCurrency(property.acquisition_cost)}` : 'not recorded'}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '5px' }}>Total Collected</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-brand-text)' }}>
                        ${formatCurrency(totalPaid)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '5px' }}>Recovery Costs</div>
                      <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: 'var(--color-error)' }}>
                        ${formatCurrency(totalRecoveryCosts)}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', color: 'var(--color-text-light)', marginBottom: '5px' }}>Net Profit/Loss</div>
                      <div style={{
                        fontSize: netProfit === null ? '1rem' : '1.5rem',
                        fontWeight: 'bold',
                        color: netProfit === null ? '#999' : netProfit >= 0 ? 'var(--forest-green)' : '#dc3545'
                      }}>
                        {netProfit === null
                          ? 'needs acquisition cost'
                          : `${netProfit >= 0 ? '+' : ''}$${formatCurrency(netProfit)}`}
                      </div>
                    </div>
                  </div>

                  {/* Show breakdown by customer */}
                  <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid var(--color-border)' }}>
                    <div style={{ fontSize: '0.9rem', fontWeight: '600', marginBottom: '10px', color: 'var(--color-text-light)' }}>
                      Customer History:
                    </div>
                    {property.loans.map((loan, idx) => (
                      <div key={loan.id} style={{ 
                        fontSize: '0.85rem', 
                        color: 'var(--color-text-light)',
                        marginBottom: '5px',
                        paddingLeft: '10px',
                        borderLeft: '3px solid var(--color-border)'
                      }}>
                        {idx + 1}. {loan.first_name} {loan.last_name} - Paid ${formatCurrency(loan.total_paid || 0)} 
                        (Defaulted {loan.default_date ? new Date(loan.default_date).toLocaleDateString() : 'N/A'})
                      </div>
                    ))}
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* Analysis Section */}
      {loans.length > 0 && (
        <div className="card" style={{ marginTop: '30px', padding: '30px' }}>
          <h2 style={{ color: 'var(--color-brand-text)', marginBottom: '20px' }}>📈 Financial Analysis</h2>
          
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            <div>
              <h4 style={{ color: 'var(--color-text-light)', marginBottom: '10px' }}>Default Rate</h4>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-error)', margin: 0 }}>
                {((loans.length / (loans.length + 0)) * 100).toFixed(1)}%
              </p>
              <small style={{ color: 'var(--color-text-muted)' }}>Of total loans</small>
            </div>

            <div>
              <h4 style={{ color: 'var(--color-text-light)', marginBottom: '10px' }}>Average Recovery Cost</h4>
              <p style={{ fontSize: '24px', fontWeight: 'bold', color: 'var(--color-error)', margin: 0 }}>
                ${formatCurrency(totalRecoveryCosts / loans.length)}
              </p>
              <small style={{ color: 'var(--color-text-muted)' }}>Per defaulted loan</small>
            </div>

            <div>
              <h4 style={{ color: 'var(--color-text-light)', marginBottom: '10px' }}>Recovery Rate</h4>
              <p style={{ 
                fontSize: '24px', 
                fontWeight: 'bold', 
                margin: 0,
                color: totalNetRecovery >= 0 ? 'var(--forest-green)' : '#dc3545'
              }}>
                {totalBalanceLost > 0 
                  ? ((totalNetRecovery / totalBalanceLost) * 100).toFixed(1) 
                  : '0'}%
              </p>
              <small style={{ color: 'var(--color-text-muted)' }}>Net recovery vs balance lost</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DefaultedLoansReport;